import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { CartItem } from '../../types';

export interface StockProduct {
  id: number;
  product_id: number;
  quantity: number;
  store_price: number | null;
  products: {
    id: number;
    name: string;
    slug: string;
    image_url: string | null;
    msrp: number | null;
    game_id: number | null;
    games?: { name: string } | null;
  } | null;
}

interface StockProductPickerProps {
  myStoreId: number | string;
  onAddItem: (stockItem: StockProduct) => void;
  balcaoItems: CartItem[];
}

export const StockProductPicker: React.FC<StockProductPickerProps> = ({
  myStoreId,
  onAddItem,
  balcaoItems,
}) => {
  const [stockItems, setStockItems] = useState<StockProduct[]>([]);
  const [loadingStock, setLoadingStock] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGameFilter, setSelectedGameFilter] = useState<string>('all');

  useEffect(() => {
    let isMounted = true;
    const fetchStock = async () => {
      if (!myStoreId) return;
      try {
        setLoadingStock(true);
        const { data, error } = await supabase
          .from('store_products')
          .select(`
            id,
            product_id,
            quantity,
            store_price,
            products:products(
              id,
              name,
              slug,
              image_url,
              msrp,
              game_id,
              games:games(name)
            )
          `)
          .eq('store_id', myStoreId)
          .gt('quantity', 0)
          .is('deleted_at', null)
          .order('updated_at', { ascending: false });

        if (error) throw error;
        if (isMounted) {
          setStockItems((data as any) || []);
        }
      } catch (err) {
        console.error('Erro ao carregar estoque da loja para pedido balcão:', err);
      } finally {
        if (isMounted) setLoadingStock(false);
      }
    };

    fetchStock();
    return () => {
      isMounted = false;
    };
  }, [myStoreId]);

  // Extract distinct games for fast filtering
  const gamesList = useMemo(() => {
    const set = new Set<string>();
    stockItems.forEach(item => {
      const gName = item.products?.games?.name;
      if (gName) set.add(gName);
    });
    return Array.from(set);
  }, [stockItems]);

  // Filtered in-stock products
  const filteredStock = useMemo(() => {
    return stockItems.filter(item => {
      const name = item.products?.name?.toLowerCase() || '';
      const matchesSearch = !searchTerm.trim() || name.includes(searchTerm.toLowerCase().trim());
      const gName = item.products?.games?.name;
      const matchesGame = selectedGameFilter === 'all' || gName === selectedGameFilter;
      return matchesSearch && matchesGame;
    });
  }, [stockItems, searchTerm, selectedGameFilter]);

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center space-x-2.5">
          <div className="w-7 h-7 rounded-lg bg-purple-950/60 border border-purple-800/60 flex items-center justify-center text-purple-400">
            <i className="fas fa-boxes-stacked text-xs"></i>
          </div>
          <div>
            <h2 className="text-xs font-bold text-white uppercase tracking-wider">
              Estoque da Sua Loja
            </h2>
            <span className="text-[11px] text-slate-400">
              {stockItems.length} {stockItems.length === 1 ? 'produto com estoque' : 'produtos com estoque'}
            </span>
          </div>
        </div>

        {/* Game Filter */}
        {gamesList.length > 1 && (
          <div className="flex items-center space-x-1.5 overflow-x-auto pb-1 sm:pb-0 max-w-full">
            <button
              onClick={() => setSelectedGameFilter('all')}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-md transition-colors whitespace-nowrap ${
                selectedGameFilter === 'all'
                  ? 'bg-purple-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              Todos
            </button>
            {gamesList.map(g => (
              <button
                key={g}
                onClick={() => setSelectedGameFilter(g)}
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-md transition-colors whitespace-nowrap ${
                  selectedGameFilter === g
                    ? 'bg-purple-600 text-white'
                    : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                {g}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Quick Search */}
      <div className="relative">
        <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs"></i>
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Buscar produto ou carta por nome para adicionar ao balcão..."
          className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500/60 rounded-lg pl-9 pr-8 py-2 text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 text-xs"
          >
            <i className="fas fa-times"></i>
          </button>
        )}
      </div>

      {/* In-Stock List */}
      {loadingStock ? (
        <div className="py-10 flex flex-col items-center justify-center space-y-2">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-purple-500"></div>
          <p className="text-xs text-slate-400">Carregando estoque da loja...</p>
        </div>
      ) : filteredStock.length === 0 ? (
        <div className="text-center py-8 px-4 bg-slate-950 rounded-lg border border-slate-800 space-y-1.5">
          <i className="fas fa-box-open text-slate-600 text-2xl"></i>
          <p className="text-sm font-semibold text-slate-300">
            {searchTerm ? 'Nenhum produto em estoque encontrado com esse nome.' : 'Nenhum produto com estoque cadastrado na sua loja.'}
          </p>
          <p className="text-xs text-slate-500 max-w-sm mx-auto">
            Apenas produtos com quantidade maior que zero em estoque são exibidos para venda no balcão.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[460px] overflow-y-auto pr-1">
          {filteredStock.map(stockItem => {
            const prod = stockItem.products;
            if (!prod) return null;

            const price = Number(stockItem.store_price ?? prod.msrp) || 0;
            const availableStock = Number(stockItem.quantity) || 0;

            // Quantity in current counter order
            const inCart = balcaoItems.find(
              i => String(i.productId || i.id) === String(prod.id)
            );
            const qtyInCart = inCart ? inCart.quantity : 0;
            const isMaxedOut = qtyInCart >= availableStock;

            return (
              <div
                key={stockItem.id}
                className={`p-2 rounded-lg border transition-colors flex items-center space-x-3 ${
                  qtyInCart > 0
                    ? 'bg-purple-950/20 border-purple-800/60'
                    : 'bg-slate-950 border-slate-800 hover:border-slate-700'
                }`}
              >
                {/* Thumbnail */}
                <div className="w-10 h-14 rounded bg-slate-800 flex-shrink-0 overflow-hidden border border-slate-700/60 flex items-center justify-center">
                  {prod.image_url ? (
                    <img
                      src={prod.image_url}
                      alt={prod.name}
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <i className="fas fa-image text-slate-600 text-xs"></i>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-xs font-semibold text-white truncate" title={prod.name}>
                    {prod.name}
                  </h3>
                  {prod.games?.name && (
                    <p className="text-[10px] text-slate-400 truncate">{prod.games.name}</p>
                  )}
                  <div className="flex items-center space-x-2 mt-0.5">
                    <span className="text-xs font-bold text-emerald-400">
                      R$ {price.toFixed(2).replace('.', ',')}
                    </span>
                    <span className="text-[10px] text-slate-400 bg-slate-800 px-1.5 py-0.5 rounded">
                      {availableStock} un disp.
                    </span>
                  </div>
                </div>

                {/* Add Button */}
                <button
                  onClick={() => onAddItem(stockItem)}
                  disabled={isMaxedOut}
                  className={`px-2.5 py-1 rounded text-xs font-semibold transition-colors flex items-center space-x-1 flex-shrink-0 ${
                    isMaxedOut
                      ? 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700/50'
                      : qtyInCart > 0
                      ? 'bg-purple-600 hover:bg-purple-500 text-white'
                      : 'bg-slate-800 hover:bg-purple-600 text-slate-200 hover:text-white border border-slate-700'
                  }`}
                  title={isMaxedOut ? 'Limite do estoque atingido' : 'Adicionar ao balcão'}
                >
                  <i className={`fas ${isMaxedOut ? 'fa-check' : 'fa-plus'} text-[10px]`}></i>
                  <span>{qtyInCart > 0 ? `+${qtyInCart}` : 'Add'}</span>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
